import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {DashboardCategory, ListResult} from 'utils/types';
import {
  WithOrderingURLParams,
  WithSearchURLParams,
  WithPaginationURLParams,
  withOrderingURLParams,
  withSearchURLParams,
  withPaginationURLParams,
} from 'utils/urlParamHelpers';

type CategoryListParams = WithOrderingURLParams<WithSearchURLParams<WithPaginationURLParams>>;
const serializeCategoryListParams = withOrderingURLParams(withSearchURLParams(withPaginationURLParams()));

export type CategoryCreatePayload = {
  name: string;
};
export type CategoryUpdatePayload = {
  id: number | string;
  name: string;
};
export interface IState {
  categories: ListResult<DashboardCategory>;
  categoryToEdit: DashboardCategory | null;
}

export const categoryEndpoints = {
  createCategory: createAsyncThunk(
    'category/createCategory',
    async (payload: CategoryCreatePayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/dashboard_category/', payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  getCategoryList: createAsyncThunk('category/getCategoryList', async (payload: CategoryListParams) => {
    const params = new URLSearchParams();
    serializeCategoryListParams(params, payload);
    const response = await ApiService.get(`/dashboard_category/?${params.toString()}`);
    return response.data;
  }),
  updateCategory: createAsyncThunk(
    'category/updateCategory',
    async (payload: CategoryUpdatePayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.put(`/dashboard_category/${payload.id}/`, payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  deleteCategory: createAsyncThunk('category/deleteCategory', async (id: number | string, {rejectWithValue}) => {
    try {
      await ApiService.delete(`/dashboard_category/${id}/`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }),
};

export const categorySlice = createSlice({
  name: 'category',
  initialState: {
    categories: {as: 'idle'},
    categoryToEdit: null,
  } as IState,
  reducers: {
    setCategoryToEdit(state, action) {
      state.categoryToEdit = action.payload;
    },
  },
  extraReducers: builder => {
    addResultCases(builder, categoryEndpoints.getCategoryList, 'categories');
  },
});

export const categoryActions = categorySlice.actions;

export type CategorySlice = {
  [categorySlice.name]: ReturnType<(typeof categorySlice)['reducer']>;
};
